import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import BusinessIcon from "@mui/icons-material/Business";
import FacebookIcon from "@mui/icons-material/Facebook";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import TwitterIcon from "@mui/icons-material/Twitter";

import {
  TABLE_ICON_BLUE_SX,
  TABLE_EXPLORE_MENU_ITEM_SX,
  TABLE_ICON_GREEN_SX,
  TABLE_ROW_HOVER_SX,
  TABLE_STYLES,
} from "../../../theme/tablestyles";
import { SELECT_MENU_PROPS } from "../../../theme/filterstyles";
import { INCUBATOR_PIPELINE_OPTIONS } from "./pipelineOptions";

const getWebsiteUrl = (website) =>
  website?.startsWith("http") ? website : `https://${website}`;

const STATUS_CONFIG = {
  "Prospect-Active":                    { bg: "#FFF8E1", color: "#E65100", border: "#FFB300" },
  "Prospect-Inactive":                  { bg: "#F5F5F5", color: "#616161", border: "#9E9E9E" },
  "Active-Early Stage":                 { bg: "#E3F2FD", color: "#0D47A1", border: "#1976D2" },
  "Active-Growth Stage":                { bg: "#E8F5E9", color: "#2E7D32", border: "#43A047" },
  "Graduated-Moved Within the Landing": { bg: "#EDE9FE", color: "#5B21B6", border: "#7C3AED" },
  "Graduated-Left Region":              { bg: "#F3E8FF", color: "#6D28D9", border: "#8B5CF6" },
  "Moved/Relocated":                    { bg: "#FEF3C7", color: "#92400E", border: "#D97706" },
  "Inactive":                           { bg: "#F5F5F5", color: "#374151", border: "#6B7280" },
};
const NO_STATUS_CONFIG = { bg: "#F3F4F6", color: "#9CA3AF", border: "#E5E7EB" };
const STATUS_TOOLTIPS = {
  "Prospect-Active": "A prospective incubator company currently being pursued.",
  "Prospect-Inactive": "A prospective incubator company that is not currently being pursued.",
  "Active-Early Stage": "Located at the incubator and in an early stage of growth.",
  "Active-Growth Stage": "Located at the incubator and in an active growth stage.",
  "Graduated-Moved Within the Landing": "Graduated from the incubator and moved within the same landing.",
  "Graduated-Left Region": "Graduated from the incubator and left the region.",
  "Moved/Relocated": "The company has moved or relocated.",
  "Inactive": "The company is no longer active.",
  "No Status": "No pipeline status has been assigned yet.",
};
const DEFAULT_STATUS_TOOLTIP = "Current incubator pipeline status.";

const EXPLORE_COLUMN_WIDTH = 88;
const NAME_COLUMN_WIDTH = 240;
const HEAD_CELL_SX = {
  backgroundColor: "#00336C",
  color: "#ffffff",
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
  borderBottom: "none",
  py: 1.5,
};
const HEADER_TRIGGER_SX = {
  minWidth: 140,
  minHeight: 28,
  px: 1.5,
  py: 0.35,
  fontWeight: 700,
  fontSize: "0.72rem",
  color: "#00336C",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  borderRadius: 1.5,
  border: "1.5px solid rgba(255,255,255,0.35)",
  backgroundColor: "rgba(255,255,255,0.92)",
  "&:hover": { backgroundColor: "#ffffff", borderColor: "rgba(255,255,255,0.8)" },
};
const FILTER_MENU_WIDTH = 360;
const FILTER_MENU_MAX_WIDTH = "calc(100vw - 32px)";

const renderText = (value, maxWidth = 180) => (
  <Tooltip title={value || ""}>
    <span
      style={{
        display: "block",
        width: "100%",
        maxWidth,
        margin: "0 auto",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        textAlign: "center",
      }}
    >
      {value || "-"}
    </span>
  </Tooltip>
);

function IncubatorsTable({
  incubators,
  columns,
  orderBy,
  orderDirection,
  onSort,
  pipelineStatusFilter,
  onPipelineStatusFilterChange,
  pipelineStatusOptions,
  selectedIds,
  allSelected,
  someSelected,
  toggleOne,
  togglePage,
  onEdit,
  onOpenProfile,
  onUpdatePipelineStatus,
}) {
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [exploreAnchorEl, setExploreAnchorEl] = useState(null);
  const [activeExploreIncubator, setActiveExploreIncubator] = useState(null);
  const [statusChangeAnchor, setStatusChangeAnchor] = useState(null);
  const [statusChangeIncubator, setStatusChangeIncubator] = useState(null);
  const pipelineFilterOpen = Boolean(filterAnchor);
  const isStatusFilterActive = pipelineStatusFilter !== "";

  const handleOpenPipelineFilter = useCallback((event) => {
    setFilterAnchor(event.currentTarget);
  }, []);

  const handleClosePipelineFilter = useCallback(() => {
    setFilterAnchor(null);
  }, []);

  const handleSelectPipelineFilter = useCallback(
    (value) => {
      onPipelineStatusFilterChange(value);
      handleClosePipelineFilter();
    },
    [handleClosePipelineFilter, onPipelineStatusFilterChange],
  );

  const handleOpenExploreMenu = useCallback((event, incubator) => {
    setExploreAnchorEl(event.currentTarget);
    setActiveExploreIncubator(incubator);
  }, []);

  const handleCloseExploreMenu = useCallback(() => {
    setExploreAnchorEl(null);
    setActiveExploreIncubator(null);
  }, []);

  const exploreActions = useMemo(() => {
    if (!activeExploreIncubator) return [];

    const linkActions = [
      {
        key: "website",
        label: "Website",
        icon: <LanguageIcon sx={{ ...TABLE_ICON_BLUE_SX, fontSize: 22 }} />,
        href: activeExploreIncubator.website,
      },
      {
        key: "facebook",
        label: "Facebook",
        icon: <FacebookIcon sx={{ ...TABLE_ICON_BLUE_SX, fontSize: 22 }} />,
        href: activeExploreIncubator.facebook,
      },
      {
        key: "linkedin",
        label: "LinkedIn",
        icon: <LinkedInIcon sx={{ ...TABLE_ICON_BLUE_SX, fontSize: 22 }} />,
        href: activeExploreIncubator.linkedin,
      },
      {
        key: "twitter",
        label: "Twitter / X",
        icon: <TwitterIcon sx={{ ...TABLE_ICON_BLUE_SX, fontSize: 22 }} />,
        href: activeExploreIncubator.twitter,
      },
    ]
      .filter((action) => action.href)
      .map((action) => ({
        ...action,
        onClick: () =>
          window.open(getWebsiteUrl(action.href), "_blank", "noopener,noreferrer"),
      }));

    return [
      {
        key: "profile",
        label: "Tech Incubator Profile",
        icon: <BusinessIcon sx={{ ...TABLE_ICON_GREEN_SX, fontSize: 22 }} />,
        onClick: () => onOpenProfile(activeExploreIncubator),
      },
      ...linkActions,
    ];
  }, [activeExploreIncubator, onOpenProfile]);

  return (
    <>
      <TableContainer
        component="div"
        sx={{
          background: "#00336C",
          overflow: "hidden",
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Table size="small" sx={{ minWidth: { xs: 1180, md: 1480 }, borderCollapse: "separate", borderSpacing: 0 }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" padding="checkbox" sx={HEAD_CELL_SX}>
                <Checkbox
                  indeterminate={someSelected && !allSelected}
                  checked={allSelected}
                  onChange={togglePage}
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<CheckCircleIcon />}
                  indeterminateIcon={<RemoveCircleIcon />}
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#ffffff" },
                  }}
                />
              </TableCell>

              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  align="center"
                  sx={{
                    ...HEAD_CELL_SX,
                    ...(col.id === "company_name" && {
                      width: NAME_COLUMN_WIDTH,
                      maxWidth: NAME_COLUMN_WIDTH,
                    }),
                  }}
                >
                  {col.id === "company_name" ? (
                    <Button
                      variant="text"
                      onClick={handleOpenPipelineFilter}
                      sx={{
                        ...HEADER_TRIGGER_SX,
                        color: isStatusFilterActive ? "#648315" : "#00336C",
                        border: isStatusFilterActive ? "1.5px solid #648315" : HEADER_TRIGGER_SX.border,
                        "&:hover": {
                          backgroundColor: "#ffffff",
                          borderColor: isStatusFilterActive ? "#648315" : "rgba(255,255,255,0.8)",
                        },
                      }}
                      endIcon={<ArrowDropDownIcon sx={{ fontSize: "1.15rem" }} />}
                    >
                      {col.label}
                    </Button>
                  ) : col.sortable ? (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? orderDirection : "asc"}
                      onClick={() => onSort(col.id)}
                      sx={{
                        width: "100%",
                        justifyContent: "center",
                        position: "relative",
                        color: "#ffffff !important",
                        "&:hover": { color: "#ffffff !important" },
                        "&.Mui-active": { color: "#ffffff !important" },
                        "& .MuiTableSortLabel-icon": {
                          position: "absolute",
                          right: -18,
                          color: "#ffffff !important",
                        },
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody sx={{ background: "white" }}>
            {incubators.map((incubator, index) => {
              const baseStriped = index % 2 === 0 ? "#fafafa" : "white";

              return (
                <TableRow
                  key={incubator.id ?? index}
                  sx={{
                    ...TABLE_ROW_HOVER_SX,
                    backgroundColor: baseStriped,
                    "&:hover": { backgroundColor: "#eef2f7" },
                  }}
                >
                  <TableCell align="center" padding="checkbox">
                    <Checkbox
                      checked={selectedIds.has(String(incubator.id))}
                      onChange={() => toggleOne(String(incubator.id))}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleIcon />}
                    />
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      ...TABLE_STYLES.bodyCell,
                      width: NAME_COLUMN_WIDTH,
                      maxWidth: NAME_COLUMN_WIDTH,
                    }}
                  >
                    <Stack direction="column" spacing={0.4} alignItems="center">
                      <Tooltip title={incubator.company_name || ""}>
                        <Button
                          variant="text"
                          sx={{
                            ...TABLE_STYLES.clickableCell,
                            fontWeight: "bold",
                          }}
                          onClick={() => onEdit(incubator)}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              maxWidth: NAME_COLUMN_WIDTH - 32,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {incubator.company_name}
                          </span>
                        </Button>
                      </Tooltip>
                      {(() => {
                        const cfg = STATUS_CONFIG[incubator.pipeline_status] || NO_STATUS_CONFIG;
                        const label = incubator.pipeline_status || "No Status";
                        return (
                          <Tooltip
                            title={STATUS_TOOLTIPS[label] || DEFAULT_STATUS_TOOLTIP}
                            arrow
                            placement="top"
                          >
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setStatusChangeAnchor(e.currentTarget);
                                setStatusChangeIncubator(incubator);
                              }}
                              endIcon={<ArrowDropDownIcon sx={{ fontSize: "0.8rem !important", ml: "-4px" }} />}
                              sx={{
                                height: 18,
                                fontSize: "0.63rem",
                                fontWeight: 600,
                                textTransform: "none",
                                borderRadius: 10,
                                px: 0.75,
                                py: 0,
                                minWidth: 0,
                                lineHeight: 1.2,
                                backgroundColor: cfg.bg,
                                color: cfg.color,
                                border: `1px solid ${cfg.border}`,
                                "&:hover": {
                                  backgroundColor: cfg.bg,
                                  borderColor: cfg.color,
                                  opacity: 0.85,
                                },
                                "& .MuiButton-endIcon": { ml: 0.25 },
                              }}
                            >
                              {label}
                            </Button>
                          </Tooltip>
                        );
                      })()}
                    </Stack>
                  </TableCell>

                <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                  {renderText(incubator.address, 220)}
                </TableCell>
                <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                  {renderText(incubator.contact_name, 180)}
                </TableCell>
                <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                  {renderText(incubator.contact_telephone, 160)}
                </TableCell>
                <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                  {incubator.contact_email ? (
                    <Link href={`mailto:${incubator.contact_email}`} underline="hover">
                      {renderText(incubator.contact_email, 220)}
                    </Link>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    ...TABLE_STYLES.bodyCell,
                    minWidth: EXPLORE_COLUMN_WIDTH,
                  }}
                >
                  <Tooltip title="Explore incubator">
                    <IconButton
                      size="small"
                      onClick={(event) => handleOpenExploreMenu(event, incubator)}
                      sx={{ p: 0.5 }}
                    >
                      <TravelExploreIcon sx={TABLE_ICON_BLUE_SX} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        open={pipelineFilterOpen}
        anchorEl={filterAnchor}
        onClose={handleClosePipelineFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        marginThreshold={16}
        MenuListProps={{
          dense: true,
          sx: {
            py: 0,
            maxHeight: 520,
            width: FILTER_MENU_WIDTH,
            maxWidth: FILTER_MENU_MAX_WIDTH,
            boxSizing: "border-box",
            overflowX: "hidden",
          },
        }}
        PaperProps={{
          ...SELECT_MENU_PROPS.PaperProps,
          sx: {
            ...(SELECT_MENU_PROPS.PaperProps?.sx || {}),
            width: FILTER_MENU_WIDTH,
            maxWidth: FILTER_MENU_MAX_WIDTH,
            boxSizing: "border-box",
            maxHeight: 560,
            overflowX: "hidden",
            overflowY: "auto",
            py: 0,
            "& .MuiTextField-root": {
              maxWidth: "100%",
            },
          },
        }}
      >
        <MenuItem
          selected={orderBy === "company_name" && orderDirection === "asc"}
          onClick={() => { onSort("company_name", "asc"); handleClosePipelineFilter(); }}
        >
          Company Name A → Z
        </MenuItem>
        <MenuItem
          selected={orderBy === "company_name" && orderDirection === "desc"}
          onClick={() => { onSort("company_name", "desc"); handleClosePipelineFilter(); }}
        >
          Company Name Z → A
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <Typography
          sx={{ px: 2, py: 0.5, fontSize: "0.68rem", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em" }}
        >
          Filter by Status
        </Typography>
        <MenuItem
          selected={pipelineStatusFilter === ""}
          onClick={() => handleSelectPipelineFilter("")}
        >
          All Statuses
        </MenuItem>
        {pipelineStatusOptions.map((option) => (
          <MenuItem
            key={option}
            selected={pipelineStatusFilter === option}
            onClick={() => handleSelectPipelineFilter(option)}
          >
            {option}
          </MenuItem>
        ))}
        <MenuItem
          selected={pipelineStatusFilter === "__none__"}
          onClick={() => handleSelectPipelineFilter("__none__")}
        >
          No Status
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={statusChangeAnchor}
        open={Boolean(statusChangeAnchor)}
        onClose={() => { setStatusChangeAnchor(null); setStatusChangeIncubator(null); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ dense: true }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 190,
              borderRadius: 2,
              border: "1px solid rgba(0,51,108,0.15)",
              boxShadow: "0 8px 24px rgba(0,51,108,0.12)",
            },
          },
        }}
      >
        <MenuItem
          selected={!statusChangeIncubator?.pipeline_status}
          onClick={() => {
            onUpdatePipelineStatus(statusChangeIncubator.id, "");
            setStatusChangeAnchor(null);
            setStatusChangeIncubator(null);
          }}
        >
          No Status
        </MenuItem>
        {INCUBATOR_PIPELINE_OPTIONS.map((opt) => (
          <MenuItem
            key={opt}
            selected={statusChangeIncubator?.pipeline_status === opt}
            onClick={() => {
              onUpdatePipelineStatus(statusChangeIncubator.id, opt);
              setStatusChangeAnchor(null);
              setStatusChangeIncubator(null);
            }}
          >
            {opt}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={exploreAnchorEl}
        open={Boolean(exploreAnchorEl)}
        onClose={handleCloseExploreMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 210,
              borderRadius: 2,
              border: "1px solid rgba(0, 51, 108, 0.12)",
              boxShadow: "0 10px 24px rgba(0, 51, 108, 0.16)",
              mt: 0.5,
            },
          },
        }}
      >
        {exploreActions.map((action) => (
          <MenuItem
            key={action.key}
            sx={TABLE_EXPLORE_MENU_ITEM_SX}
            onClick={() => {
              handleCloseExploreMenu();
              action.onClick();
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>{action.icon}</ListItemIcon>
            <ListItemText primary={action.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default React.memo(IncubatorsTable);
